import { useState, useEffect, useMemo } from 'react';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { ResponsiveLine } from '@nivo/line';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import { idiomaAtual } from '../../i18n';
import { formataDataExibicao } from '../../common/utils';

const PhenylalanineHistoryChart = ({ atendimentoId }) => {
    const { t } = useTranslation(['atendimentos']);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    // guarda a chave da mensagem; é traduzida no render
    const [error, setError] = useState(null);

    const formataNivel = (valor) => new Intl.NumberFormat(idiomaAtual(), { minimumFractionDigits: 2 }).format(valor);

    useEffect(() => {
        const fetchData = async () => {
            if (!atendimentoId) return;

            setLoading(true);
            try {
                const response = await api.get(`/atendimentos/historico-fenilalanina/${atendimentoId}/`);
                setData(response.data);
                setError(null);
            } catch (err) {
                console.error("Erro ao buscar histórico de fenilalanina:", err);
                setError("atendimentos:phe.loadError");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [atendimentoId]);

    const chartData = useMemo(() => {
        if (!data || data.length === 0) return [];

        const seriesData = data.map(item => ({
            x: formataDataExibicao(item.data),
            y: parseFloat(item.valor) || 0,
            originalData: item
        }));

        return [
            {
                id: t('atendimentos:phe.series'),
                data: seriesData
            }
        ];
    }, [data, t]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{t(error)}</Alert>;
    }

    if (!data || data.length === 0) {
        return null;
    }

    return (
        <Card className="mb-4 shadow-sm">
            <Card.Header className="bg-transparent border-0 pb-0">
                <h6 className="fw-bold mb-0 text-dark">{t('atendimentos:phe.title')}</h6>
            </Card.Header>
            <Card.Body style={{ height: '400px' }}>
                <ResponsiveLine
                    data={chartData}
                    margin={{ top: 40, right: 40, bottom: 50, left: 40 }}
                    xScale={{ type: 'point' }}
                    yScale={{
                        type: 'linear',
                        min: 'auto',
                        max: 'auto',
                        stacked: false,
                        reverse: false
                    }}
                    yFormat={formataNivel}
                    curve="monotoneX"
                    enableArea={true}
                    areaOpacity={0.15}
                    colors={['#0d6efd']}
                    axisTop={null}
                    axisRight={null}
                    enableGridX={false}
                    axisBottom={{
                        tickSize: 0,
                        tickPadding: 15,
                        tickRotation: 0,
                        legend: '',
                        legendOffset: 36,
                        legendPosition: 'middle'
                    }}
                    axisLeft={null}
                    pointSize={8}
                    pointColor={{ theme: 'background' }}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    enablePointLabel={true}
                    pointLabel={d => formataNivel(d.data.y)}
                    pointLabelYOffset={-14}
                    useMesh={true}
                    enableSlices="x"
                    sliceTooltip={({ slice }) => {
                        return (
                            <div
                                style={{
                                    background: 'white',
                                    padding: '9px 12px',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {slice.points.map(point => (
                                    <div key={point.id} style={{ padding: '3px 0' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{point.data.x}</div>
                                        <div style={{ color: point.serieColor }}>
                                            <strong>{t('atendimentos:phe.level')}</strong> {formataNivel(point.data.y)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    }}
                    legends={[]} 
                />
            </Card.Body>
        </Card>
    );
};

export default PhenylalanineHistoryChart;
